import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import SubscriptionWidget from "@/components/SubscriptionWidget";
import BulkDeleteAllModal from "@/components/BulkDeleteAllModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Check, KeyRound, Trash2, Info, Zap, Skull, Plug, Clock, FolderTree } from "lucide-react";

export default function Settings() {
  const [providers, setProviders] = useState([]);
  const [saved, setSaved] = useState([]);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState("");
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [cleanupBusy, setCleanupBusy] = useState("");
  const [cleanupReport, setCleanupReport] = useState(null);

  const analyzeCategories = async () => {
    setCleanupBusy("analyze");
    try {
      const { data } = await api.post("/erp/nuvemshop/categories/cleanup", { dry_run: true });
      setCleanupReport(data);
      const dups = data.merge_plan.reduce((a, m) => a + m.remove.length, 0);
      if (dups === 0 && data.unused.length === 0) {
        toast.success("Nenhuma categoria duplicada ou sem uso encontrada");
      } else {
        toast.info(`${dups} duplicada(s) e ${data.unused.length} sem uso — revise e clique em Executar limpeza`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao analisar categorias");
    } finally {
      setCleanupBusy("");
    }
  };

  const executeCleanup = async () => {
    const dups = cleanupReport?.merge_plan?.reduce((a, m) => a + m.remove.length, 0) || 0;
    if (!window.confirm(
      `Isso vai:\n• Mesclar ${dups} categoria(s) duplicada(s) movendo ${cleanupReport?.products_to_move || 0} produto(s)\n• Apagar ${cleanupReport?.unused?.length || 0} categoria(s) sem produtos\n\nA ação não pode ser desfeita. Continuar?`
    )) return;
    setCleanupBusy("run");
    try {
      const { data } = await api.post("/erp/nuvemshop/categories/cleanup", { dry_run: false });
      setCleanupReport(data);
      if (data.partial) {
        toast.warning(`Limpeza parcial: ${data.moved} movidos, ${data.deleted} apagadas. Clique em Executar novamente para continuar.`, { duration: 10000 });
      } else {
        toast.success(`Limpeza concluída: ${data.moved} produto(s) movido(s), ${data.deleted} categoria(s) apagada(s)`);
      }
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} erro(s): ${data.errors[0]}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha na limpeza de categorias");
    } finally {
      setCleanupBusy("");
    }
  };

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/erp/providers"),
      api.get("/erp/credentials"),
    ]);
    setProviders(p.data);
    setSaved(s.data);
  };

  useEffect(() => {
    load();
    // Handle OAuth callback query params
    if (searchParams.get("bling_connected")) {
      toast.success("Bling conectado com sucesso!");
      setSearchParams({}, { replace: true });
    }
    const blingErr = searchParams.get("bling_error");
    if (blingErr) {
      toast.error(`Falha ao conectar Bling: ${blingErr}`);
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("nuvemshop_connected")) {
      toast.success("Nuvemshop conectada com sucesso!");
      setSearchParams({}, { replace: true });
    }
    const err = searchParams.get("nuvemshop_error");
    if (err) {
      toast.error(`Falha ao conectar Nuvemshop: ${err}`);
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("ml_connected")) {
      toast.success("Mercado Livre conectado com sucesso!");
      setSearchParams({}, { replace: true });
    }
    const mlErr = searchParams.get("ml_error");
    if (mlErr) {
      toast.error(`Falha ao conectar Mercado Livre: ${mlErr}`);
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("billing") === "trial_started") {
      toast.success("Trial de 7 dias iniciado! Você pode publicar até 10 produtos.", { duration: 8000 });
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("billing") === "success") {
      toast.success("Assinatura confirmada!", { duration: 6000 });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const savedFor = (key) => saved.find((x) => x.provider === key);

  const onChange = (providerKey, field, val) => {
    setValues((prev) => ({
      ...prev,
      [providerKey]: { ...(prev[providerKey] || {}), [field]: val },
    }));
  };

  const onSave = async (provider) => {
    setBusy(provider.key);
    try {
      await api.post("/erp/credentials", {
        provider: provider.key,
        credentials: values[provider.key] || {},
      });
      toast.success(`Credenciais do ${provider.name} salvas`);
      setValues((prev) => ({ ...prev, [provider.key]: {} }));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setBusy("");
    }
  };

  const onOAuthConnect = async (provider) => {
    setBusy(provider.key);
    try {
      const { data } = await api.post(provider.oauth_start_endpoint.replace("/api", ""));
      // Redirect current tab (so cookie/state persists cleanly)
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao iniciar OAuth");
      setBusy("");
    }
  };

  const onDelete = async (providerKey) => {
    if (!window.confirm(`Remover credenciais do ${providerKey}?`)) return;
    try {
      await api.delete(`/erp/credentials/${providerKey}`);
      toast.success("Credenciais removidas");
      await load();
    } catch (e) {
      toast.error("Erro ao remover");
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 p-6 sm:p-10">
        <Toaster position="top-right" richColors />

        <header className="mb-8">
          <div className="eyebrow mb-2">Configurações</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B]">
            Integrações com ERPs
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Conecte seu ERP para popular fichas de produto com as imagens encontradas.
            Suas credenciais são criptografadas antes de serem armazenadas.
          </p>
        </header>

        {/* Subscription (Mercado Pago) */}
        <SubscriptionWidget />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {providers
            // Show Nuvemshop, Bling, Mercado Livre in the top row (first 3
            // columns) — everything else follows in insertion order.
            .slice()
            .sort((a, b) => {
              const priority = { nuvemshop: 0, bling: 1, mercadolivre: 2 };
              return (priority[a.key] ?? 99) - (priority[b.key] ?? 99);
            })
            .map((p) => {
            const s = savedFor(p.key);
            return (
              <div
                key={p.key}
                className="border border-zinc-200 rounded-md bg-white p-5"
                data-testid={`erp-card-${p.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-[#002FA7] text-white flex items-center justify-center">
                      <KeyRound className="w-4 h-4" />
                    </div>
                    <div className="font-display font-semibold text-lg text-[#09090B]">{p.name}</div>
                  </div>
                  {s && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono tracking-widest text-emerald-700 bg-emerald-50 px-2 py-1 rounded-sm">
                      <Check className="w-3 h-3" /> CONECTADO
                    </span>
                  )}
                </div>

                <div className="flex items-start gap-1.5 text-xs text-zinc-500 mb-4 pl-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{p.help}</span>
                </div>

                {p.oauth_supported && (
                  <Button
                    onClick={() => onOAuthConnect(p)}
                    disabled={busy === p.key}
                    className="w-full h-10 mb-3 rounded-md bg-gradient-to-r from-[#002FA7] to-[#0055FF] text-white font-medium hover:opacity-95"
                    data-testid={`erp-${p.key}-oauth-button`}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {s ? "Reconectar " + p.name : "Conectar " + p.name}
                  </Button>
                )}

                {p.oauth_supported && (
                  <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest text-center mb-3">
                    ou preencha manualmente
                  </div>
                )}

                <div className="space-y-3">
                  {p.credential_fields.map((f) => (
                    <div key={f.key}>
                      <Label className="eyebrow text-xs">
                        {f.label}
                        {s && (
                          <span className="ml-2 font-mono text-zinc-400 tracking-normal normal-case">
                            atual: {s.credentials[f.key] || "—"}
                          </span>
                        )}
                      </Label>
                      <Input
                        type={f.type}
                        placeholder={s ? "Deixe em branco para manter" : ""}
                        value={values[p.key]?.[f.key] || ""}
                        onChange={(e) => onChange(p.key, f.key, e.target.value)}
                        className="mt-1.5 h-10 rounded-md border-zinc-300 font-mono text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                        data-testid={`erp-${p.key}-${f.key}-input`}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-5">
                  <Button
                    onClick={() => onSave(p)}
                    disabled={busy === p.key}
                    variant={p.oauth_supported ? "outline" : "default"}
                    className={`flex-1 rounded-md ${
                      p.oauth_supported ? "border-zinc-300" : "brand-primary text-white"
                    } transition-colors`}
                    data-testid={`erp-${p.key}-save-button`}
                  >
                    {busy === p.key ? "Salvando..." : s ? "Atualizar" : "Salvar credenciais"}
                  </Button>
                  {s && (
                    <Button
                      onClick={() => onDelete(p.key)}
                      variant="outline"
                      className="rounded-md border-zinc-300"
                      data-testid={`erp-${p.key}-delete-button`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Coming-soon ERP integrations */}
        <div className="mt-6">
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">Próximas integrações</div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {[
              { key: "tiny", name: "Tiny ERP", desc: "ERP líder para pequenas lojas — sincroniza produtos, estoque e pedidos." },
              { key: "omie", name: "Omie", desc: "Plataforma de gestão para PMEs com módulo completo de e-commerce." },
            ].map((prov) => (
              <div
                key={prov.key}
                className="bg-zinc-50 border border-zinc-200 rounded-lg p-5 opacity-60 relative"
                data-testid={`erp-coming-soon-${prov.key}`}
              >
                <div className="absolute top-3 right-3 bg-zinc-300 text-zinc-700 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm">
                  Disponível em breve!
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Plug className="w-4 h-4 text-zinc-500" />
                  <h3 className="font-bold text-zinc-600">{prov.name}</h3>
                </div>
                <p className="text-sm text-zinc-500">{prov.desc}</p>
                <Button
                  disabled
                  variant="outline"
                  className="mt-4 w-full rounded-md border-zinc-300 text-zinc-400 cursor-not-allowed h-10"
                >
                  Conectar (em breve)
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* iter 54 — Nuvemshop category housekeeping */}
        <div className="mt-10 border border-zinc-200 bg-white rounded-lg p-5" data-testid="ns-category-cleanup-card">
          <div className="flex items-start gap-3">
            <FolderTree className="w-5 h-5 text-[#002FA7] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-zinc-900">Organizar categorias da Nuvemshop</h3>
              <p className="text-sm text-zinc-600 mt-1">
                Junta categorias <strong>duplicadas com o mesmo nome</strong> em uma só (movendo os produtos) e
                apaga categorias <strong>sem nenhum produto</strong> — assim os filtros da sua loja não repetem categorias.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  onClick={analyzeCategories}
                  disabled={cleanupBusy}
                  variant="outline"
                  className="rounded-md border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 h-10"
                  data-testid="ns-cleanup-analyze-button"
                >
                  {cleanupBusy === "analyze" ? "Analisando..." : "Analisar categorias"}
                </Button>
                {cleanupReport && (cleanupReport.merge_plan.length > 0 || cleanupReport.unused.length > 0) && (
                  <Button
                    onClick={executeCleanup}
                    disabled={cleanupBusy}
                    className="rounded-md brand-primary text-white h-10"
                    data-testid="ns-cleanup-execute-button"
                  >
                    {cleanupBusy === "run" ? "Executando..." : "Executar limpeza"}
                  </Button>
                )}
              </div>
              {cleanupReport && (
                <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs space-y-2" data-testid="ns-cleanup-report">
                  <div className="font-mono text-zinc-700">
                    {cleanupReport.categories_total} categorias na loja ·{" "}
                    <strong>{cleanupReport.merge_plan.reduce((a, m) => a + m.remove.length, 0)}</strong> duplicada(s) p/ mesclar ·{" "}
                    <strong>{cleanupReport.unused.length}</strong> sem uso p/ apagar ·{" "}
                    {cleanupReport.products_to_move} produto(s) serão movidos
                  </div>
                  {cleanupReport.merge_plan.slice(0, 8).map((m, i) => (
                    <div key={i} className="text-zinc-600">
                      <strong>{m.name}</strong>: mantém #{m.keep_id}, remove {m.remove.map((r) => `#${r.id} (${r.products} prod.)`).join(", ")}
                    </div>
                  ))}
                  {cleanupReport.merge_plan.length > 8 && (
                    <div className="text-zinc-500">... e mais {cleanupReport.merge_plan.length - 8} grupo(s)</div>
                  )}
                  {cleanupReport.unused.length > 0 && (
                    <div className="text-zinc-600">
                      Sem uso: {cleanupReport.unused.slice(0, 10).map((u) => u.name).join(", ")}
                      {cleanupReport.unused.length > 10 && ` e mais ${cleanupReport.unused.length - 10}...`}
                    </div>
                  )}
                  {!cleanupReport.dry_run && (
                    <div className="text-emerald-700 font-semibold" data-testid="ns-cleanup-result">
                      ✓ {cleanupReport.moved} produto(s) movido(s) · {cleanupReport.deleted} categoria(s) apagada(s)
                      {cleanupReport.partial && " · PARCIAL — clique em Executar novamente para continuar"}
                      {cleanupReport.errors?.length > 0 && ` · ${cleanupReport.errors.length} erro(s)`}
                    </div>
                  )}
                  {cleanupReport.merge_plan.length === 0 && cleanupReport.unused.length === 0 && (
                    <div className="text-emerald-700">✓ Nenhuma duplicata ou categoria sem uso — loja organizada!</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Danger zone */}
        <div className="mt-10 border-2 border-red-300 bg-red-50/40 rounded-lg p-5" data-testid="danger-zone">
          <div className="flex items-start gap-3">
            <Skull className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800">Zona de Perigo</h3>
              <p className="text-sm text-zinc-700 mt-1">
                Apague em lote <strong>TODOS</strong> os produtos publicados pelo Magic SIP em qualquer plataforma conectada (Nuvemshop, Bling, Mercado Livre).
                A ação exige confirmação por frase + motivo (auditoria) e é <strong>irreversível</strong>.
              </p>

              <div
                className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900"
                data-testid="sync-delay-notice"
              >
                <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Tempo de sincronização:</strong> ao publicar ou excluir produtos, aguarde de <strong>10 a 20 minutos</strong> para que a plataforma (Nuvemshop / Bling / Mercado Livre) atualize o catálogo público. Esse tempo é do ERP, não do Magic SIP.
                </div>
              </div>

              <Button
                onClick={() => setShowBulkDelete(true)}
                variant="outline"
                className="mt-3 rounded-md border-red-300 text-red-700 hover:bg-red-100 h-10"
                data-testid="open-bulk-delete-all"
              >
                <Skull className="w-4 h-4 mr-2" />
                Apagar TODOS os publicados
              </Button>
            </div>
          </div>
        </div>

        <BulkDeleteAllModal
          open={showBulkDelete}
          onClose={() => setShowBulkDelete(false)}
        />
      </main>
    </div>
  );
}
